import axios, { AxiosError, AxiosRequestHeaders } from "axios";
import { v4 as uuidv4 } from "uuid";
import { useAuth } from "../context/auth-context";
import { handoffAuthHeader } from "./handoffToken";

// Define your custom hook
const useAuthAxios = () => {
  const { logout } = useAuth();

  const handleError = (error: AxiosError) => {
    // If the error has a response from the server
    if (error.response) {
      const { status, data } = error.response as any;

      // Handle session expiration
      if (
        (status === 401 &&
          data?.message ===
            "Invalid or expired session. Please sign in again.") ||
        (status === 401 && data?.message === "Invalid token, please log in again")
      ) {
        logout();
      }

      // The backend allowlists request origins by exact match (this is what replaced CSRF
      // tokens). A 403 here means we're calling the wrong backend domain, or the dev proxy
      // isn't rewriting Origin — not a user-facing problem, and silently swallowing it
      // costs hours.
      if (status === 403 && data?.message === "Request origin not allowed") {
        console.error(
          `[auth] Origin ${window.location.origin} is not on the backend allowlist. ` +
            `In dev, check src/setupProxy.js rewrites Origin; in prod, check the app is ` +
            `calling the backend on its own domain.`
        );
      }

      return {
        status,
        message: data?.message || "An error occurred",
        data,
      };
    }
    // If the error was a request but no response
    if (error.request) {
      return {
        status: null,
        message: "No response received from server",
        data: null,
      };
    }
    // Fallback for other errors
    return {
      status: null,
      message: error.message || "An unknown error occurred",
      data: null,
    };
  };

  const baseAxios = axios.create({
    baseURL: process.env.REACT_APP_API_URL,
    timeout: 30000,
    // The session is an httpOnly cookie we can't read, so it has to ride along
    // automatically. Without this the browser won't send it at all.
    withCredentials: true,
  });

  baseAxios.interceptors.request.use(
    async (config) => {
      if (!config.headers) {
        config.headers = {} as AxiosRequestHeaders;
      }

      // Cookie auth needs no Authorization header. The only exception is the mobile→web
      // KYC handoff — see utils/handoffToken.ts, which is temporary.
      if (!config.headers.Authorization) {
        Object.assign(config.headers, handoffAuthHeader());
      }

      // Money-moving calls pin their own key per attempt (hooks/useIdempotencyKey)
      // so retries dedupe — never overwrite one that's already set.
      if (config.method?.toLowerCase() === "post") {
        const hasIdempotencyKey = Object.keys(config.headers).some(
          (key) => key.toLowerCase() === "idempotency-key"
        );
        if (!hasIdempotencyKey) {
          config.headers["idempotency-Key"] = uuidv4();
        }
      }

      return config;
    },
    (error) => Promise.reject(handleError(error))
  );

  baseAxios.interceptors.response.use(
    (response) => response,
    (error) => Promise.reject(handleError(error))
  );

  return baseAxios;
};

export default useAuthAxios;
